import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const team = [
  {
    name: "林晚",
    role: "联合创始人 · 主编",
    bio: "十年时尚摄影经验，相信光线能讲出比语言更动人的故事。",
    avatar:
      "https://readdy.ai/api/search-image?query=portrait%20of%20an%20elegant%20asian%20woman%20photographer%2C%20warm%20soft%20studio%20light%2C%20cream%20background%2C%20professional%20friendly%2C%20high%20detail&width=600&height=600&seq=team-01&orientation=squarish",
  },
  {
    name: "沈鹿",
    role: "艺术总监",
    bio: "专注形体与抽象影像，用镜头探索女性形态的诗意边界。",
    avatar:
      "https://readdy.ai/api/search-image?query=portrait%20of%20a%20thoughtful%20young%20asian%20woman%20artist%2C%20warm%20natural%20light%2C%20minimal%20cream%20background%2C%20high%20detail&width=600&height=600&seq=team-02&orientation=squarish",
  },
  {
    name: "苏芷",
    role: "内容策划",
    bio: "把日常拍成大片的人，擅长在生活里发现温暖的瞬间。",
    avatar:
      "https://readdy.ai/api/search-image?query=portrait%20of%20a%20cheerful%20asian%20woman%20creative%20director%2C%20warm%20soft%20light%2C%20cream%20background%2C%20friendly%20expressive%2C%20high%20detail&width=600&height=600&seq=team-03&orientation=squarish",
  },
  {
    name: "周颂",
    role: "影像顾问",
    bio: "偏爱东方美学与大地色系，让传统在当代语境里重新呼吸。",
    avatar:
      "https://readdy.ai/api/search-image?query=portrait%20of%20a%20calm%20asian%20woman%20photography%20consultant%2C%20warm%20earthy%20light%2C%20minimal%20beige%20background%2C%20high%20detail&width=600&height=600&seq=team-04&orientation=squarish",
  },
];

const timeline = [
  { year: "2021", title: "平台萌芽", desc: "三位摄影师在一次展览后决定搭建一个专注女性影像的展示窗口。" },
  { year: "2022", title: "正式上线", desc: "「女性ins大片」上线，首批收录 200 余件精选作品，获得广泛关注。" },
  { year: "2023", title: "灵感专题", desc: "推出灵感专题栏目，邀请策展人系统梳理女性摄影的多元主题。" },
  { year: "2024", title: "创作社区", desc: "开放摄影师入驻，合作创作者超过 120 位，作品数量突破 1800 件。" },
  { year: "2025", title: "走向国际", desc: "引入海外创作者，让东方女性影像被更多人看见。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 女性ins大片摄影作品平台",
    description:
      "了解女性ins大片的品牌故事、团队理念与发展历程。我们是一个专注展示最新女性INS风格大片、分享摄影灵感的视觉艺术平台。",
    keywords: "关于我们,女性ins大片,品牌故事,摄影团队,女性视觉艺术平台",
    path: "/about",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        {/* Hero */}
        <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20toned%20creative%20photography%20studio%20with%20soft%20natural%20light%2C%20elegant%20minimal%20interior%20with%20camera%20and%20photo%20prints%2C%20artistic%20warm%20beige%20palette%2C%20high%20detail&width=1920&height=900&seq=about-hero-01&orientation=landscape"
            alt="女性ins大片 - 关于我们的品牌形象"
            title="女性ins大片 - 女性摄影作品平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/75 via-foreground-950/40 to-foreground-950/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pb-10 md:pb-14">
              <nav className="flex items-center gap-2 text-xs text-background-100/80 mb-4" aria-label="面包屑">
                <Link to="/" className="hover:text-accent-400 cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-50">关于我们</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50">
                关于女性ins大片
              </h1>
              <p className="text-background-100/85 text-sm md:text-base mt-3 max-w-2xl">
                一个专注女性视觉艺术的摄影作品展示与灵感分享平台
              </p>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                Our Story
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-4">
                让女性的美，被更多种方式看见
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                「女性ins大片」诞生于一群摄影师的共同愿望：为女性影像建立一个真正属于自己的展示空间。我们汇聚时尚大片、艺术摄影、生活写真与创意特辑四大方向，既呈现高级质感，也保留真实温度。
              </p>
              <p>
                我们希望这里不只是一个看图的地方，更是一个获取灵感的窗口。无论是想要学习创作的摄影爱好者，还是寻找视觉参考的时尚与艺术从业者，都能在这里找到打动自己的那一帧。
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-12">
            {[
              { num: "1800+", label: "精选作品", icon: "ri-image-2-line" },
              { num: "120+", label: "合作创作者", icon: "ri-user-star-line" },
              { num: "36", label: "灵感专题", icon: "ri-lightbulb-line" },
              { num: "5", label: "覆盖风格方向", icon: "ri-palette-line" },
            ].map((s) => (
              <div key={s.label} className="bg-secondary-50 rounded-lg p-5 md:p-6">
                <span className="w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center mb-4">
                  <i className={`${s.icon} text-foreground-950 text-lg`}></i>
                </span>
                <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{s.num}</p>
                <p className="text-xs md:text-sm text-foreground-600 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
                Our Team
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
                幕后的创作者们
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((m, i) => (
                <div
                  key={m.name}
                  className="bg-background-50 rounded-lg overflow-hidden animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="w-full aspect-square overflow-hidden">
                    <img
                      src={m.avatar}
                      alt={`${m.name} - 女性ins大片团队成员`}
                      title={`${m.name} - ${m.role}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900">{m.name}</h3>
                    <p className="text-xs text-primary-600 mb-2">{m.role}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{m.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
              Journey
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
              我们的发展历程
            </h2>
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-[7px] md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-background-300" />
            {timeline.map((item, i) => (
              <div
                key={item.year}
                className={`relative flex gap-6 md:gap-0 mb-8 last:mb-0 ${
                  i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-1.5 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50 z-10" />
                <div className={`pl-8 md:pl-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-10 md:text-right" : "md:pl-10"}`}>
                  <span className="font-heading text-xl font-bold text-accent-600">{item.year}</span>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-14 md:pb-20">
          <div className="bg-primary-500 rounded-lg px-6 md:px-12 py-10 md:py-14 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              有想分享的女性摄影作品？
            </h2>
            <p className="text-background-50/85 text-sm md:text-base mb-7 max-w-xl mx-auto">
              无论你是专业摄影师还是热爱记录的创作者，都欢迎联系我们，让更多人看见你的作品。
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              联系我们
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}